/**
 * @file apps/web/src/components/CSVImportModal.tsx
 * @description Modal for bulk-importing test scripts via CSV upload.
 * Uses papaparse for parsing. Shows column mapping UI, preview table, and validation errors.
 * Calls onImport(rows) prop — caller handles the API call.
 */

import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { X, Upload, FileText, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CSVImportRow {
  title:        string;
  module:       string;
  description?: string;
  steps?:       string;  // pipe-separated step descriptions
}

interface ColumnMapping {
  title:        string;
  module:       string;
  description:  string;
  steps:        string;
}

interface CSVImportModalProps {
  open:      boolean;
  onClose:   () => void;
  onImport:  (rows: CSVImportRow[]) => Promise<void>;
  projectId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REQUIRED_FIELDS: (keyof ColumnMapping)[] = ['title', 'module'];
const ALL_FIELDS: (keyof ColumnMapping)[] = ['title', 'module', 'description', 'steps'];

const FIELD_LABELS: Record<keyof ColumnMapping, string> = {
  title:       'Title *',
  module:      'Module *',
  description: 'Description',
  steps:       'Steps (pipe-separated)',
};

const MODULES = [
  'finance','procurement','sales','inventory',
  'manufacturing','hr','payroll','projects','assets','other',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function guessMapping(headers: string[]): ColumnMapping {
  const lower = headers.map((h) => h.toLowerCase().trim());
  const find = (...candidates: string[]): string => {
    for (const c of candidates) {
      const idx = lower.findIndex((h) => h.includes(c));
      if (idx !== -1) return headers[idx]!;
    }
    return '';
  };

  return {
    title:       find('title', 'name', 'script'),
    module:      find('module', 'area', 'category'),
    description: find('description', 'desc', 'summary'),
    steps:       find('steps', 'step', 'procedure'),
  };
}

function validateRow(row: CSVImportRow, idx: number): string | null {
  if (!row.title?.trim()) return `Row ${idx + 1}: "title" is required`;
  if (!row.module?.trim()) return `Row ${idx + 1}: "module" is required`;
  if (!MODULES.includes(row.module.toLowerCase().trim())) {
    return `Row ${idx + 1}: module "${row.module}" is not valid`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Column mapper row
// ---------------------------------------------------------------------------

function MappingRow({
  field,
  value,
  headers,
  onChange,
}: {
  field:    keyof ColumnMapping;
  value:    string;
  headers:  string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono text-ink/70 w-40 shrink-0">
        {FIELD_LABELS[field]}
      </span>
      <div className="relative flex-1">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="select text-xs py-1.5 pr-8 w-full"
        >
          <option value="">(not mapped)</option>
          {headers.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview table
// ---------------------------------------------------------------------------

function PreviewTable({
  rows,
  errors,
}: {
  rows:   CSVImportRow[];
  errors: string[];
}) {
  const errorRows = new Set(
    errors.map((e) => {
      const m = e.match(/Row (\d+)/);
      return m ? parseInt(m[1]!) - 1 : -1;
    })
  );

  return (
    <div className="overflow-auto max-h-48 rounded-lg border border-[rgba(8,17,31,0.08)]">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray/60">
            <th className="table-header text-left px-3 py-2 w-6">#</th>
            <th className="table-header text-left px-3 py-2">Title</th>
            <th className="table-header text-left px-3 py-2 w-28">Module</th>
            <th className="table-header text-left px-3 py-2">Description</th>
            <th className="table-header text-left px-3 py-2 w-16">Steps</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 10).map((row, i) => (
            <tr
              key={i}
              className={[
                'border-t border-[rgba(8,17,31,0.05)]',
                errorRows.has(i) ? 'bg-danger/5' : 'hover:bg-sky/20',
              ].join(' ')}
            >
              <td className="px-3 py-1.5 font-mono text-muted">{i + 1}</td>
              <td className="px-3 py-1.5 text-ink font-medium truncate max-w-[180px]">
                {row.title || <span className="text-danger italic">missing</span>}
              </td>
              <td className="px-3 py-1.5 font-mono text-ink/70">
                {row.module || <span className="text-danger italic">missing</span>}
              </td>
              <td className="px-3 py-1.5 text-ink/60 truncate max-w-[200px]">
                {row.description ?? '—'}
              </td>
              <td className="px-3 py-1.5 text-center font-mono text-muted">
                {row.steps
                  ? row.steps.split('|').filter(Boolean).length
                  : 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 10 && (
        <div className="px-3 py-2 text-[11px] font-mono text-muted border-t border-[rgba(8,17,31,0.05)] bg-gray/30">
          + {rows.length - 10} more rows (showing first 10)
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSVImportModal
// ---------------------------------------------------------------------------

export function CSVImportModal({ open, onClose, onImport, projectId: _projectId }: CSVImportModalProps) {
  const [rawHeaders, setRawHeaders]   = useState<string[]>([]);
  const [rawData, setRawData]         = useState<Record<string, string>[]>([]);
  const [mapping, setMapping]         = useState<ColumnMapping>({ title: '', module: '', description: '', steps: '' });
  const [errors, setErrors]           = useState<string[]>([]);
  const [importing, setImporting]     = useState(false);
  const [fileName, setFileName]       = useState('');
  const [step, setStep]               = useState<'upload' | 'map' | 'preview'>('upload');

  const reset = () => {
    setRawHeaders([]);
    setRawData([]);
    setMapping({ title: '', module: '', description: '', steps: '' });
    setErrors([]);
    setFileName('');
    setStep('upload');
  };

  const handleClose = () => { reset(); onClose(); };

  const onDrop = useCallback((accepted: File[]) => {
    const file = accepted[0];
    if (!file) return;
    setFileName(file.name);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const headers = result.meta.fields ?? [];
        setRawHeaders(headers);
        setRawData(result.data);
        setMapping(guessMapping(headers));
        setErrors([]);
        setStep('map');
      },
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'text/plain': ['.csv', '.txt'] },
    maxFiles: 1,
  });

  // Build mapped rows from rawData + mapping
  const mappedRows: CSVImportRow[] = rawData.map((row) => ({
    title:       mapping.title       ? (row[mapping.title] ?? '').trim()       : '',
    module:      mapping.module      ? (row[mapping.module] ?? '').toLowerCase().trim() : '',
    description: mapping.description ? (row[mapping.description] ?? '').trim() : undefined,
    steps:       mapping.steps       ? (row[mapping.steps] ?? '').trim()       : undefined,
  }));

  const validate = (): boolean => {
    const errs = mappedRows
      .map((r, i) => validateRow(r, i))
      .filter((e): e is string => e !== null);
    setErrors(errs);
    return errs.length === 0;
  };

  const handleNext = () => {
    if (step === 'map') {
      if (validate()) setStep('preview');
    }
  };

  const handleImport = async () => {
    if (!validate()) return;
    setImporting(true);
    try {
      await onImport(mappedRows);
      handleClose();
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'Import failed']);
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={handleClose}
        className="fixed inset-0 bg-ink/20 backdrop-blur-[2px] z-40 animate-[fade-in_150ms_ease]"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-label="Import Test Scripts from CSV"
      >
        <div className="bg-white rounded-2xl shadow-modal w-full max-w-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(8,17,31,0.07)]">
            <div>
              <h2 className="font-display text-base font-semibold text-ink">Import Test Scripts</h2>
              <p className="text-xs font-mono text-muted mt-0.5">
                Upload a CSV, map columns, then import.
              </p>
            </div>
            <button onClick={handleClose} className="btn-icon text-muted hover:text-ink">
              <X size={18} />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-0 px-6 pt-4 pb-2">
            {(['upload', 'map', 'preview'] as const).map((s, i) => (
              <div key={s} className="flex items-center">
                <div className={[
                  'flex items-center gap-1.5 text-xs font-mono font-medium',
                  step === s ? 'text-blue' : s < step ? 'text-success' : 'text-muted',
                ].join(' ')}>
                  <span className={[
                    'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                    step === s ? 'bg-blue text-white' : s < step ? 'bg-success/15 text-success' : 'bg-gray text-muted',
                  ].join(' ')}>
                    {i + 1}
                  </span>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </div>
                {i < 2 && (
                  <div className="w-8 h-px bg-[rgba(8,17,31,0.1)] mx-2" />
                )}
              </div>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

            {/* Step 1: Upload */}
            {step === 'upload' && (
              <div
                {...getRootProps()}
                className={[
                  'border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors',
                  isDragActive
                    ? 'border-blue bg-sky/40'
                    : 'border-[rgba(8,17,31,0.12)] hover:border-blue/50 hover:bg-sky/20',
                ].join(' ')}
              >
                <input {...getInputProps()} />
                <Upload size={32} className={['mx-auto mb-3', isDragActive ? 'text-blue' : 'text-muted'].join(' ')} />
                <p className="text-sm font-medium text-ink">
                  {isDragActive ? 'Drop your CSV here' : 'Drag & drop a CSV file, or click to browse'}
                </p>
                <p className="text-xs font-mono text-muted mt-1.5">
                  .csv or .txt · first row must be headers
                </p>
                <p className="text-[11px] text-muted/70 mt-3">
                  Required columns: <span className="font-mono">title</span>, <span className="font-mono">module</span>
                </p>
              </div>
            )}

            {/* Step 2: Map columns */}
            {step === 'map' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-sky rounded-lg">
                  <FileText size={16} className="text-blue shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-ink">{fileName}</p>
                    <p className="text-[11px] font-mono text-muted">
                      {rawData.length} rows · {rawHeaders.length} columns detected
                    </p>
                  </div>
                </div>

                <div>
                  <p className="label mb-2">Column Mapping</p>
                  <div className="space-y-2">
                    {ALL_FIELDS.map((field) => (
                      <MappingRow
                        key={field}
                        field={field}
                        value={mapping[field]}
                        headers={rawHeaders}
                        onChange={(v) => setMapping((m) => ({ ...m, [field]: v }))}
                      />
                    ))}
                  </div>
                </div>

                {errors.length > 0 && (
                  <div className="space-y-1">
                    {errors.slice(0, 3).map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-danger">
                        <AlertCircle size={12} className="shrink-0" />
                        {e}
                      </div>
                    ))}
                    {errors.length > 3 && (
                      <p className="text-xs text-muted font-mono">…and {errors.length - 3} more errors</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Preview */}
            {step === 'preview' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-success shrink-0" />
                  <p className="text-sm font-medium text-ink">
                    {mappedRows.length} scripts ready to import
                  </p>
                </div>

                <PreviewTable rows={mappedRows} errors={errors} />

                {errors.length > 0 && (
                  <div className="p-3 bg-danger/8 rounded-lg space-y-1">
                    {errors.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-danger">
                        <AlertCircle size={11} className="shrink-0" />
                        {e}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[rgba(8,17,31,0.07)] flex items-center justify-between gap-3">
            <button
              onClick={step === 'upload' ? handleClose : () => setStep(step === 'preview' ? 'map' : 'upload')}
              className="btn-ghost text-sm"
            >
              {step === 'upload' ? 'Cancel' : 'Back'}
            </button>

            <div className="flex items-center gap-2">
              {step === 'map' && (
                <button
                  onClick={handleNext}
                  disabled={!mapping.title || !mapping.module}
                  className="btn-primary"
                >
                  Preview →
                </button>
              )}
              {step === 'preview' && (
                <button
                  onClick={handleImport}
                  disabled={importing || errors.length > 0}
                  className="btn-primary"
                >
                  {importing ? 'Importing…' : `Import ${mappedRows.length} Scripts`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
